import { createReducer, on } from '@ngrx/store';
import { LoadingStatus, Payment, PaymentCategory, PaymentFee, SwiftCode, Transfer } from '@qvs/core';
import { fees } from '~/testing/mocks/fees';
import * as fromPaymentActions from './payments.actions';
import {
  OrdersPaymentRequest,
  OrdersPaymentRequestStatusEnum,
  Uuid
} from '~/lazy/payments/pages/new-payment/models/ordersPaymentRequest';
import {
  paymentStatusUpdateMaxTimeInMilliseconds,
  updatePaymentStatusIntervalInMilliseconds
} from '~/lazy/payments/models/payment.settings';

export const paymentsFeatureKey = 'payments';

export interface State {
  fees: PaymentFee[];
  feesLoading?: boolean;
  recentPayments: Payment[];
  recentPaymentsLoadingStatus: LoadingStatus;
  singlePaymentLoadingStatus: LoadingStatus;
  recentTransfers: Transfer[];
  recentTransferLoadingStatus: LoadingStatus;
  swiftCodes: SwiftCode[];
  ibanSwiftCodeMapping?: { [iban: string]: SwiftCode };
  swiftCodesIsLoading?: boolean;
  categories: PaymentCategory[];
  categoriesIsLoading?: boolean;
  ordersPaymentRequests: Map<Uuid, OrdersPaymentRequest>;
  // TODO: 16-09-2026/braune: Is this needed at all?
  ordersPaymentRequestsUpdateCount: Map<Uuid, number>;
}

export const initialState: State = {
  fees,
  feesLoading: false,
  recentPayments: [],
  recentPaymentsLoadingStatus: LoadingStatus.IDLE,
  singlePaymentLoadingStatus: LoadingStatus.IDLE,
  swiftCodes: [],
  swiftCodesIsLoading: false,
  categories: [],
  categoriesIsLoading: false,
  ordersPaymentRequests: new Map<Uuid, OrdersPaymentRequest>(),
  ordersPaymentRequestsUpdateCount: new Map<Uuid, number>(),
  recentTransfers: [],
  recentTransferLoadingStatus: LoadingStatus.IDLE
};

export const reducer = createReducer(
  initialState,
  /** recent payments **/
  on(fromPaymentActions.loadPaymentRecentPayments, (state) => ({
    ...state,
    recentPaymentsLoadingStatus: LoadingStatus.LOADING
  })),
  on(fromPaymentActions.loadPaymentRecentPaymentsSuccess, (state, action) => ({
    ...state,
    recentPaymentsLoadingStatus: LoadingStatus.SUCCEEDED,
    recentPayments: action.payments ?? state.recentPayments
  })),
  on(fromPaymentActions.loadPaymentRecentPaymentsFailure, (state) => ({
    ...state,
    recentPaymentsLoadingStatus: LoadingStatus.FAILED
  })),
  /** payments **/
  on(fromPaymentActions.loadPayment, (state) => ({
    ...state,
    singlePaymentLoadingStatus: LoadingStatus.LOADING
  })),
  on(fromPaymentActions.loadPaymentSuccess, (state, action) => {
    const paymentAlreadyExists = state.recentPayments.some((payment) => payment.id === action.payment.id);

    return {
      ...state,
      singlePaymentLoadingStatus: LoadingStatus.SUCCEEDED,
      recentPayments: paymentAlreadyExists ? state.recentPayments : [...state.recentPayments, action.payment]
    };
  }),
  on(fromPaymentActions.loadPaymentFailure, (state) => ({
    ...state,
    singlePaymentLoadingStatus: LoadingStatus.FAILED
  })),
  /** recent transfers **/
  on(fromPaymentActions.loadPaymentRecentTransfers, (state) => ({
    ...state,
    recentTransferLoadingStatus: LoadingStatus.LOADING
  })),
  on(fromPaymentActions.loadPaymentRecentTransfersSuccess, (state, action) => ({
    ...state,
    recentTransferLoadingStatus: LoadingStatus.SUCCEEDED,
    recentTransfers: action.transfers
  })),
  on(fromPaymentActions.loadPaymentRecentTransfersFailure, (state) => ({
    ...state,
    recentTransferLoadingStatus: LoadingStatus.FAILED
  })),
  /** swift codes **/
  on(fromPaymentActions.loadPaymentSwiftCodes, (state) => ({
    ...state,
    swiftCodesIsLoading: true
  })),
  on(fromPaymentActions.loadPaymentSwiftCodesSuccess, (state, action) => ({
    ...state,
    swiftCodesIsLoading: false,
    swiftCodes: action.swiftCodes
  })),
  on(fromPaymentActions.loadPaymentSwiftCodesFailure, (state) => ({
    ...state,
    swiftCodesIsLoading: false
  })),
  /** categories **/
  on(fromPaymentActions.loadPaymentCategories, (state) => ({
    ...state,
    categoriesIsLoading: true
  })),
  on(fromPaymentActions.loadPaymentCategoriesSuccess, (state, action) => ({
    ...state,
    categoriesIsLoading: false,
    categories: action.categories
  })),
  on(fromPaymentActions.loadPaymentCategoriesFailure, (state) => ({
    ...state,
    categoriesIsLoading: false
  })),
  on(
    /** order payment **/
    fromPaymentActions.createOrdersPaymentAskForExecutionSuccess,
    fromPaymentActions.createOrdersPayment,
    fromPaymentActions.createOrdersPaymentFailure,
    fromPaymentActions.createOrdersPaymentSuccess,
    /** authorize payment **/
    fromPaymentActions.authorizeOrdersPaymentSuccess,
    fromPaymentActions.authorizeOrdersPaymentFailure,
    /** confirm payment **/
    fromPaymentActions.confirmAuthorizedOrdersPaymentSuccess,
    fromPaymentActions.confirmAuthorizedOrdersPaymentFailure,
    /** payment creation success **/
    fromPaymentActions.newPaymentCreatedSuccess,
    (state, action) => ({
      ...state,
      ordersPaymentRequests: new Map(state.ordersPaymentRequests).set(
        action.ordersPaymentRequest.uuid,
        action.ordersPaymentRequest
      )
    })
  ),
  on(
    fromPaymentActions.createOrdersPaymentCanceled,
    /** payment creation success **/
    fromPaymentActions.createOrdersPaymentSuccessUserHasReadSuccess,
    (state, action) => {
      const uuid = action.ordersPaymentRequest.uuid;
      const ordersPaymentRequests = new Map(state.ordersPaymentRequests);
      const ordersPaymentRequestsUpdateCount = new Map(state.ordersPaymentRequestsUpdateCount);

      ordersPaymentRequests.delete(uuid);
      ordersPaymentRequestsUpdateCount.delete(uuid);

      return {
        ...state,
        ordersPaymentRequests,
        ordersPaymentRequestsUpdateCount
      };
    }
  ),
  /** payment orders status updates **/
  on(fromPaymentActions.updatePaymentOrdersRequests, (state, action) => ({
    ...state,
    ordersPaymentRequestsUpdateCount: new Map(
      Array.from(action.requests.values()).map((request) => [
        request.uuid,
        (state.ordersPaymentRequestsUpdateCount.get(request.uuid) ?? 0) + 1
      ])
    )
  })),
  on(fromPaymentActions.updatePaymentOrdersRequestsSuccess, (state, action) => ({
    ...state,
    ordersPaymentRequests: new Map(action.requests)
  })),
  on(fromPaymentActions.updatePaymentOrdersRequestsFailure, (state, action) => ({
    ...state,
    ordersPaymentRequests: new Map(
      Array.from(action.requests.values()).map((request) => [
        request.uuid,
        state.ordersPaymentRequestsUpdateCount.has(request.uuid) ||
        state.ordersPaymentRequestsUpdateCount.get(request.uuid) <
          paymentStatusUpdateMaxTimeInMilliseconds / updatePaymentStatusIntervalInMilliseconds
          ? request
          : { ...request, status: OrdersPaymentRequestStatusEnum.userMaxStatusUpdateFailure }
      ])
    )
  })),
  /** swift codes from iban **/
  on(fromPaymentActions.loadSwiftCodeFromIbanSuccess, (state, action) => {
    const ibanSwiftCodeMapping = { ...(state.ibanSwiftCodeMapping || {}) };
    ibanSwiftCodeMapping[action.iban] = action.swiftCode;
    return {
      ...state,
      ibanSwiftCodeMapping
    };
  })
);
